<script setup>
import axios from '@/axios'
import MeetingSelectBox from "@/components/common/input/meetingSelectBox.vue";
import getBlobDuration from 'get-blob-duration';
import {useLanguageStore, useLoadingStore, useLoginStore} from "@/pinia";
import {onMounted, ref, toRef, computed} from "vue";
import {useToast} from "@/composables/useToast";
import * as tus from 'tus-js-client'
import MeetingType from "@/components/common/input/meetingType.vue";

const toast = useToast();
const languageStore = useLanguageStore();
const loadingStore = useLoadingStore();
const loginStore = useLoginStore();

// Form refs
const insertFormRef = ref(null);
const updateFormRef = ref(null);

// Validation rules
const rules = {
  required: v => !!v || languageStore.get('requiredField')
};

const props = defineProps(['modalData', 'selectedItem', 'currentFolder'])
const percent = ref(0);
const folderId = toRef(props, 'currentFolder');
const inputValue = ref({
  videoId: '',
  videoTitle: '',
  meetingId: '',
  file: null,
  videoDescription: '',
  openYn: ''
})
const snsYn = ref([])
const videoDuration = ref(null);
const emits = defineEmits(['close', 'insertModalClose'])
const meetingType = ref();
const isChangeSns = ref(false);

const isInsertMode = computed(() => props.modalData === 'insert' || props.modalData === 'dragAndDrop')
const isUpdateMode = computed(() => props.modalData === 'update')

const set_meetingType = (v) => {
  meetingType.value = v;
}

const videoValidate = async () => {
  let result = true;

  // v-form validation
  const {valid} = await insertFormRef.value.validate();

  // Custom validation for file
  if (!inputValue.value.file) {
    toast.warning(languageStore.get('selectFile'));
    result = false;
    loadingStore.set_isLoading(false);
    return result;
  }

  if (!valid) {
    toast.warning(languageStore.get('pleaseEnterRequiredFields'));
    result = false;
    loadingStore.set_isLoading(false);
  } else {
    try {
      const file = await getFileObject(inputValue.value.file);
      videoDuration.value = await getBlobDuration(file);
    } catch (error) {
      console.error(error);
      result = false;
      loadingStore.set_isLoading(false);
    }
  }
  return result;
}

const selectFile = (event) => {
  if (event.target.files.length > 0) {
    inputValue.value.file = event.target.files[0];
    inputValue.value.name = event.target.files[0].name;
  } else {
    toast.warning(languageStore.get('selectFile'));
  }
}

const selectMeeting = (value) => {
  inputValue.value.meetingId = value;
}

const closeBtn = () => {
  emits('close')
}

const tusUpload = async () => {
  loadingStore.set_isLoading(true);

  if (await videoValidate()) {
    const file = await getFileObject(inputValue.value.file);
    const chunkSize = 1024 * 1024 * 5;
    let response;
    let baseUrl = process.env.VUE_APP_API_URL;

    const upload = new tus.Upload(file, {
      endpoint: baseUrl + "/tus/upload",
      chunkSize,
      retryDelay: [0],
      metadata: {
        filename: file.name,
        filetype: file.type,
        fileSize: file.size,
        folderId: folderId.value,
        videoTitle: inputValue.value.videoTitle,
        meetingId: meetingType.value !== null && meetingType.value !== 'mt000' ? inputValue.value.meetingId : "",
        videoDescription: inputValue.value.videoDescription,
        openYn: inputValue.value.openYn ? 'Y' : 'N',
        videoTime: videoDuration.value,
        delYn: 'N',
        snsType: snsYn.value,
        meetingType: meetingType.value ? meetingType.value : null,
        regUser: loginStore.loginInfo?.userId || ''
      },
      onError: (e) => {
        if (e.originalRequest) {
          if (confirm(`Failed \n Do you want retry?`)) {
            upload.start();
          } else {
            loadingStore.set_isLoading(false);
          }
        }
      },
      onProgress: (bytesUploaded, bytesTotal) => {
        percent.value = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
      },
      onSuccess: () => {
        emits('insertModalClose');
        loadingStore.set_isLoading(false);
      },
      onAfterResponse: (req, res) => {
        response = res.getBody();
      },
    });

    upload.findPreviousUploads().then((previousUploads) => {
      if (previousUploads.length) {
        upload.resumeFromPreviousUpload(previousUploads[0]);
      }
      upload.start();
    })
  }
}

const update = async () => {
  // v-form validation
  const {valid} = await updateFormRef.value.validate();
  if (!valid) {
    toast.warning(languageStore.get('pleaseEnterRequiredFields'));
    return;
  }

  loadingStore.set_isLoading(true);
  const data = {
    videoId: inputValue.value.videoId,
    videoTitle: inputValue.value.videoTitle,
    meetingType: meetingType.value,
    meetingId: meetingType.value == null || meetingType.value == 'mt000' ? "" : inputValue.value.meetingId,
    videoDescription: inputValue.value.videoDescription,
    privateStatus: inputValue.value.openYn ? "Y" : "N",
    isChangeSns: isChangeSns.value
  }

  axios.put("/admin/video/update", data, {headers: {'Content-Type': 'application/json'}})
      .then((e) => {
        emits('insertModalClose');
        loadingStore.set_isLoading(false);
      })
      .catch((error) => {
        console.log(error);
        loadingStore.set_isLoading(false);
      })
}

async function getFileObject(fileEntryOrFile) {
  return new Promise((resolve, reject) => {
    if (fileEntryOrFile.isFile) {
      fileEntryOrFile.file(file => resolve(file), reject);
    } else if (fileEntryOrFile instanceof File) {
      resolve(fileEntryOrFile);
    } else {
      reject(new Error('The argument must be a File or FileEntry object'));
    }
  });
}

onMounted(() => {
  if (props.modalData === 'update') {
    inputValue.value.videoId = props.selectedItem.id
    inputValue.value.videoTitle = props.selectedItem.videoTitle
    inputValue.value.meetingId = props.selectedItem.meetingId
    inputValue.value.file = props.selectedItem.file
    inputValue.value.videoDescription = props.selectedItem.videoDescription
    meetingType.value = props.selectedItem.meetingType

    if (props.selectedItem.privateStatus == 'Y') {
      inputValue.value.openYn = true
    } else {
      inputValue.value.openYn = false
    }

    const snsLists = props.selectedItem.snsVideo
    if (snsLists) {
      for (let snsVideo of snsLists) {
        snsYn.value.push(snsVideo.snsType)
      }
    }
  } else if (props.modalData === 'dragAndDrop') {
    inputValue.value.file = props.selectedItem.file
  }
})
</script>

<template>
  <!-- Insert / Drag and Drop Modal -->
  <v-dialog
      :model-value="isInsertMode"
      @update:model-value="closeBtn"
      width="600"
      persistent
  >
    <v-card>
      <v-card-title class="modal-header">
        {{ languageStore.get('videoRegistration') }}
      </v-card-title>

      <v-card-text class="modal-body">
        <v-form ref="insertFormRef">
          <v-text-field
              v-model="inputValue.videoTitle"
              :label="languageStore.get('title') + ' *'"
              variant="outlined"
              density="compact"
              :rules="[rules.required]"
              class="mb-4"
          />

          <!-- 회의 유형 / 회의 선택 -->
          <v-row>
            <v-col cols="5">
              <meeting-type
                  @set_meetingType="set_meetingType"
                  :data="{'codeId': 'mt000', 'codeNm': languageStore.get('notSelected')}"
                  :label="languageStore.get('meetingType')"
              />
            </v-col>
            <v-col cols="7" v-if="meetingType !== 'mt000' && meetingType">
              <meeting-select-box
                  :meeting-type="meetingType"
                  @set_meetingId="selectMeeting"
                  :label="languageStore.get('meeting')"
              />
            </v-col>
          </v-row>

          <v-file-input
              @change="selectFile"
              v-model="inputValue.file"
              :label="languageStore.get('file') + ' *'"
              variant="outlined"
              density="compact"
              hide-details="auto"
              prepend-icon=""
              prepend-inner-icon="mdi-file-video"
              class="mb-4"
          >
            <template v-slot:selection="{ fileNames }">
              <template v-for="fileName in fileNames" :key="fileName">
                <v-chip class="me-2" color="primary" size="small" label>{{ fileName }}</v-chip>
              </template>
            </template>
          </v-file-input>

          <v-textarea
              v-model="inputValue.videoDescription"
              :label="languageStore.get('description')"
              variant="outlined"
              density="compact"
              rows="3"
              hide-details="auto"
              class="mb-4"
          />

          <!-- 비공개 / SNS -->
          <v-row class="mt-2">
            <v-col cols="4">
              <v-switch
                  v-model="inputValue.openYn"
                  :label="languageStore.get('private')"
                  color="primary"
                  hide-details
                  density="compact"
              />
            </v-col>
            <v-col cols="4">
              <v-checkbox
                  v-model="snsYn"
                  value="youtube"
                  hide-details
                  density="compact"
              >
                <template #label>
                  <v-icon color="red" size="20" class="mr-1">mdi-youtube</v-icon>
                  <span>YouTube</span>
                </template>
              </v-checkbox>
            </v-col>
          </v-row>

          <!-- 업로드 진행률 -->
          <div v-if="loadingStore.isLoading && percent > 0" class="mt-4">
            <v-progress-linear
                :model-value="percent"
                color="primary"
                height="20"
                striped
            >
              <template v-slot:default>
                <strong>{{ percent }}%</strong>
              </template>
            </v-progress-linear>
          </div>
        </v-form>
      </v-card-text>

      <v-card-actions class="modal-footer">
        <v-progress-circular
            v-if="loadingStore.isLoading"
            color="primary"
            indeterminate
            size="24"
        />
        <v-spacer/>
        <v-btn
            v-if="!loadingStore.isLoading"
            color="primary"
            variant="flat"
            @click="tusUpload"
        >
          {{ languageStore.get('save') }}
        </v-btn>
        <v-btn
            variant="outlined"
            @click="closeBtn"
        >
          {{ languageStore.get('close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Update Modal -->
  <v-dialog
      :model-value="isUpdateMode"
      @update:model-value="closeBtn"
      width="600"
      persistent
  >
    <v-card>
      <v-card-title class="modal-header">
        {{ languageStore.get('videoEdit') }}
      </v-card-title>

      <v-card-text class="modal-body">
        <v-form ref="updateFormRef">
          <v-text-field
              v-model="inputValue.videoTitle"
              :label="languageStore.get('title') + ' *'"
              variant="outlined"
              density="compact"
              :rules="[rules.required]"
              class="mb-4"
          />

          <!-- 회의 유형 / 회의 선택 -->
          <v-row>
            <v-col cols="5">
              <meeting-type
                  :meeting="meetingType"
                  :data="{'codeId': 'mt000', 'codeNm': languageStore.get('notSelected')}"
                  @set_meetingType="set_meetingType"
                  :label="languageStore.get('meetingType')"
              />
            </v-col>
            <v-col cols="7" v-if="meetingType !== 'mt000' && meetingType">
              <meeting-select-box
                  :meeting-type="meetingType"
                  :defaultMeetingId="inputValue.meetingId"
                  @set_meetingId="selectMeeting"
                  :label="languageStore.get('meeting')"
              />
            </v-col>
          </v-row>

          <v-text-field
              :model-value="selectedItem?.videoName"
              :label="languageStore.get('file')"
              variant="outlined"
              density="compact"
              hide-details="auto"
              readonly
              disabled
              class="mb-4"
          />

          <v-textarea
              v-model="inputValue.videoDescription"
              :label="languageStore.get('description')"
              variant="outlined"
              density="compact"
              rows="3"
              hide-details="auto"
              class="mb-4"
          />

          <!-- 비공개 / SNS 수정 -->
          <v-row class="mt-2">
            <v-col cols="4">
              <v-switch
                  v-model="inputValue.openYn"
                  :label="languageStore.get('private')"
                  color="primary"
                  hide-details
                  density="compact"
              />
            </v-col>
            <v-col cols="4" v-if="snsYn && snsYn.length > 0">
              <v-checkbox
                  v-model="isChangeSns"
                  label="SNS 동시 수정"
                  hide-details
                  density="compact"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="modal-footer">
        <v-progress-circular
            v-if="loadingStore.isLoading"
            color="primary"
            indeterminate
            size="24"
        />
        <v-spacer/>
        <v-btn
            v-if="!loadingStore.isLoading"
            color="primary"
            variant="flat"
            @click="update"
        >
          {{ languageStore.get('edit') }}
        </v-btn>
        <v-btn
            variant="outlined"
            @click="closeBtn"
        >
          {{ languageStore.get('close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* 모달 헤더 */
.modal-header {
  background-color: #1A3A5C !important;
  color: #FFFFFF !important;
  padding: 16px 24px;
  font-weight: 600;
  font-size: 18px;
}

/* 모달 바디 - Vuetify 기본 padding 제거 */
:deep(.v-card-text.modal-body) {
  padding: 24px !important;
  max-height: 70vh;
  overflow-y: auto;
}

/* 모달 바디 내 입력 필드 간격 */
.modal-body :deep(.v-input) {
  margin-bottom: 16px;
}

.modal-body :deep(.v-row) {
  margin-bottom: 16px;
}

.modal-body :deep(.v-row:last-child) {
  margin-bottom: 0;
}

/* 모달 푸터 - Vuetify 기본 padding 제거 */
:deep(.v-card-actions.modal-footer) {
  padding: 16px 24px !important;
  border-top: 1px solid #DEE2E6;
  gap: 8px;
}

/* SNS 로고 */
.sns-logo {
  width: 24px;
  height: 24px;
  margin-right: 8px;
}
</style>
